/**
 * Connects one table to Firestore in both directions.
 *
 * Down: a subscription per slice. Attendance is applied as a diff against the
 * snapshot before it, so a remote update only touches the cells that actually
 * changed, and a cell edited here that is unsent or in flight is left alone.
 * Once the server's own state is known, a mark it confirmed earlier and has
 * since lost is removed here too — see `removedSince`.
 *
 * Up: the reducer's outbox is drained on a short debounce. Attendance goes as a
 * field-level merge; the structural slices go whole, since merging two people's
 * simultaneous roster edits is ambiguous in a way that per-cell marks are not.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isFirebaseConfigured,
  subscribeSlice,
  touchTable,
  writeCells,
  writeSlice,
} from './firebase';
import { SLICE_NAMES, SLICE_PAYLOAD, diffAttendance } from './slices';
import {
  listTables,
  loadConfirmedAttendance,
  saveConfirmedAttendance,
  wasMigratedFromV1,
} from '../data/storage';

const FLUSH_DELAY_MS = 500;
/** Ceiling on the retry backoff. Long enough to stop hammering, short enough
 *  that deploying corrected rules is noticed without a reload. */
const MAX_RETRY_DELAY_MS = 60000;

export function useSync({ code, table, outbox, dispatch }) {
  const [status, setStatus] = useState(code ? 'connecting' : 'off');
  const [error, setError] = useState(null);

  const tableRef = useRef(table);
  tableRef.current = table;

  const outboxRef = useRef(outbox);
  outboxRef.current = outbox;

  // Which table is open right now, so a write that settles after a table switch
  // can tell that it belongs to the previous one.
  const codeRef = useRef(code);
  codeRef.current = code;

  const lastAttendance = useRef({});
  // Whether this subscription has seen the attendance document yet.
  const baselined = useRef(false);
  // The marks the server has confirmed, as this browser last saw them. Saved
  // with the table so the next load can tell a mark deleted elsewhere from one
  // made here that never arrived — see `loadConfirmedAttendance`.
  const confirmed = useRef(null);
  // What the removal pass needs, until it has run: the record as it stood when
  // this table was opened, and the marks the table was opened with.
  const pendingRemovals = useRef(null);
  // Cells this client has written but not yet had acknowledged. Used instead of
  // the snapshot-wide `hasPendingWrites` flag to recognise our own echo.
  const inFlight = useRef(new Set());
  const flushTimer = useRef(null);
  const failures = useRef(0);
  const online = useRef(typeof navigator === 'undefined' ? true : navigator.onLine);

  /* ------------------------------------------------------------ subscribe */

  useEffect(() => {
    if (!code || !isFirebaseConfigured) {
      setStatus('off');
      return undefined;
    }

    setStatus('connecting');
    lastAttendance.current = {};
    baselined.current = false;
    confirmed.current = loadConfirmedAttendance(code);
    pendingRemovals.current = { record: confirmed.current, loaded: tableRef.current.attendance };
    inFlight.current = new Set();
    failures.current = 0;
    let cancelled = false;

    const handleError = (err) => {
      if (cancelled) return;
      setError(err);
      setStatus('error');
    };

    const receive = (slice) => (data, isLocalEcho, fromCache) => {
      if (cancelled) return;
      // A snapshot proves the connection only if the server sent it: the SDK
      // raises a cached one the moment the connection drops, and attendance
      // now hears those. Nor does a snapshot clear an error — a successful
      // write does, and a failed one must stay visible until then.
      setStatus((current) => {
        if (current === 'saving' || current === 'error') return current;
        if (!fromCache) return 'live';
        if (current === 'connecting') return online.current ? 'live' : 'offline';
        return current;
      });

      if (slice !== 'attendance') {
        // For a whole-document slice the echo flag has to be obeyed: a snapshot
        // reflecting our own earlier write does not contain edits we have made
        // since and not yet flushed, and merging it would revert them.
        if (isLocalEcho) return;
        dispatch({ type: 'remote/merge', slice, data });
        return;
      }

      // Attendance is different, and must not use the flag. `hasPendingWrites`
      // is per document, not per field, so any un-acknowledged write of ours
      // marks the whole snapshot — and with the multi-tab cache the mutation
      // queue is shared, so a second tab's pending write flags this tab's
      // snapshots too. Dropping those wholesale is why two tabs of the same
      // browser stopped seeing each other's marks.
      //
      // The diff against `lastAttendance` is already the precise echo filter:
      // it reports only what actually changed. All that has to be excluded is
      // the cells whose server value is about to be overtaken by our own
      // write: those in flight, and those edited here and not yet sent.
      // Applying a remote value to an unsent cell showed the remote value
      // while the queued edit went on to overwrite it on the server.
      //
      // The first snapshot is diffed against the saved marks the server also
      // has, so it adds and changes but never removes: it may be nothing more
      // than our own pending write laid over a document the cache did not hold.
      // A restore or a cleared table about to overwrite the document gets
      // nothing merged over it at all.
      const replacing = outboxRef.current.attendanceReplace;
      let previous = lastAttendance.current;
      if (!baselined.current) previous = replacing ? null : onlyKeysIn(tableRef.current.attendance, data);
      baselined.current = true;
      const changed = previous ? diffAttendance(previous, data) : {};

      // Removals from what the server confirmed happen once, on the first
      // snapshot that is the server's own state — from the server, with
      // nothing of ours laid over it.
      if (pendingRemovals.current && !isLocalEcho && !fromCache) {
        const { record, loaded } = pendingRemovals.current;
        pendingRemovals.current = null;
        // With no record yet, the saved copy stands in: see `assumedConfirmed`.
        const basis = record || assumedConfirmed(code, loaded);
        if (basis && !replacing) {
          Object.assign(changed, removedSince(basis, loaded, tableRef.current.attendance, data));
        }
      }

      const unsent = outboxRef.current.attendance;
      for (const key of Object.keys(changed)) {
        if (inFlight.current.has(key) || key in unsent) delete changed[key];
      }
      lastAttendance.current = data;

      // Only a snapshot with nothing of ours still pending shows what the
      // server itself holds. One with our writes on it can still say an entry
      // is out of date, and dropping an entry can only ever keep a mark.
      if (!isLocalEcho) {
        confirmed.current = data;
        saveConfirmedAttendance(code, data);
      } else if (confirmed.current) {
        const agreed = agreeing(confirmed.current, data);
        if (Object.keys(agreed).length !== Object.keys(confirmed.current).length) {
          confirmed.current = agreed;
          saveConfirmedAttendance(code, agreed);
        }
      }

      if (Object.keys(changed).length > 0) {
        dispatch({ type: 'remote/merge', slice: 'attendance', data: changed });
      }
    };

    const unsubscribes = SLICE_NAMES.map((slice) =>
      subscribeSlice(code, slice, receive(slice), handleError, {
        includeMetadataChanges: slice === 'attendance',
      })
    );

    return () => {
      cancelled = true;
      unsubscribes.forEach((fn) => fn && fn());
    };
  }, [code, dispatch]);

  /* ---------------------------------------------------------------- flush */

  const flush = useCallback(async () => {
    if (!code || !isFirebaseConfigured) return;
    // A flush left over from a table that is no longer open would read the
    // open table's outbox and send it under the old code. A join that finished
    // after the user had switched to another table did exactly that, writing a
    // local table's roster over a live shared one.
    if (codeRef.current !== code) return;

    const current = outboxRef.current;
    const slices = ['roster', 'schedule', 'settings'].filter((slice) => current[slice]);
    const cells = current.attendance;
    const cellKeys = Object.keys(cells);
    const replaceAttendance = current.attendanceReplace;
    if (slices.length === 0 && cellKeys.length === 0 && !replaceAttendance) return;

    setStatus('saving');
    // Cleared optimistically: anything edited during the write marks the outbox
    // again and is picked up by the next flush.
    dispatch({ type: 'sync/drained', slices, cells: cellKeys, attendanceReplace: replaceAttendance });

    // Everything this write needs is captured before the first await. Switching
    // tables is what makes that matter: `code` here is the table we are sending,
    // while the refs move on to whichever table is open when the write settles.
    const replacedAttendance = replaceAttendance ? { ...tableRef.current.attendance } : null;
    const name = tableRef.current.settings?.name;
    const isStillOpen = () => codeRef.current === code;

    const sent = replacedAttendance ? Object.keys(replacedAttendance) : cellKeys;
    for (const key of sent) inFlight.current.add(key);
    const releaseInFlight = () => {
      for (const key of sent) inFlight.current.delete(key);
    };

    try {
      const writes = slices.map((slice) => writeSlice(code, slice, SLICE_PAYLOAD[slice](tableRef.current)));

      if (replacedAttendance) {
        // The whole set of marks was replaced (a cleared table), so the document
        // is overwritten. Merging would leave every deleted mark in place.
        writes.push(writeSlice(code, 'attendance', replacedAttendance));
      } else if (cellKeys.length > 0) {
        writes.push(writeCells(code, cells));
      }
      await Promise.all(writes);
      releaseInFlight();

      // The baseline only moves once the write has actually landed. Advancing it
      // first meant a rejected write left us believing the server held values it
      // never received, so the next snapshot diffed them as unchanged forever.
      // It belongs to whichever table is subscribed now, so leave it alone if
      // that is no longer this one.
      if (isStillOpen()) {
        if (replacedAttendance) {
          lastAttendance.current = replacedAttendance;
        } else {
          for (const [key, value] of Object.entries(cells)) {
            if (value === null) delete lastAttendance.current[key];
            else lastAttendance.current[key] = value;
          }
        }
      }

      // An acknowledged write is confirmed, whether or not this table is still
      // the one open. With no record yet there is nothing to add it to: the
      // first confirmed snapshot creates one, and until then the saved copy
      // stands in for it (a record of only these cells would not).
      if (replacedAttendance || cellKeys.length > 0) {
        const open = isStillOpen();
        const before = open ? confirmed.current : loadConfirmedAttendance(code);
        if (replacedAttendance ? isRemembered(code) : before) {
          // Entries a later snapshot has already contradicted are dropped:
          // leaving one out can only keep a mark, never delete it. Before the
          // first snapshot of a reopened table there is nothing to check
          // against yet.
          const base =
            replacedAttendance ||
            (open && baselined.current ? agreeing(before, lastAttendance.current) : before);
          const after = applyCells(base, replacedAttendance ? {} : cells);
          saveConfirmedAttendance(code, after);
          if (open) confirmed.current = after;
        }
      }

      // Metadata only. If just this is rejected the table itself still saved,
      // so it must not turn a successful save into a reported failure.
      await touchTable(code, name).catch(() => {});
      if (!isStillOpen()) return;
      failures.current = 0;
      setError(null);
      setStatus(online.current ? 'live' : 'offline');
    } catch (err) {
      // The drain above was optimistic, so hand the changes back before
      // reporting; otherwise a rejected write throws away the edits it carried.
      // Only if we are still on this table — requeueing into a different one
      // would write these cells under the wrong code.
      releaseInFlight();
      if (!isStillOpen()) return;
      failures.current += 1;
      dispatch({ type: 'sync/requeue', slices, cells, attendanceReplace: replaceAttendance });
      setError(err);
      // Offline is a queue that will drain itself; a rejection while online is
      // not, and saying "offline" would hide a permissions problem.
      setStatus(online.current ? 'error' : 'offline');
    }
  }, [code, dispatch]);

  useEffect(() => {
    if (!code || !isFirebaseConfigured) return undefined;
    const pending =
      outbox.roster ||
      outbox.schedule ||
      outbox.settings ||
      outbox.attendanceReplace ||
      Object.keys(outbox.attendance).length > 0;
    if (!pending) return undefined;

    // Requeueing hands back a fresh outbox, which re-runs this effect — so a
    // permanently rejected write (rules not deployed, say) would otherwise
    // retry twice a second forever. Offline writes never reject; Firestore
    // queues them. So every rejection seen here is worth backing off from.
    const delay = Math.min(FLUSH_DELAY_MS * 2 ** failures.current, MAX_RETRY_DELAY_MS);

    clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flush, delay);
    return () => clearTimeout(flushTimer.current);
  }, [code, outbox, flush]);

  /**
   * Overwrites every slice in the cloud with this browser's copy — v1's
   * "Force Sync Local → Cloud". The escape hatch for when a table has drifted
   * and you know which copy is right.
   */
  const pushAll = useCallback(async () => {
    if (!code || !isFirebaseConfigured) throw new Error('This table is not shared.');
    setStatus('saving');
    try {
      const current = tableRef.current;
      await Promise.all([
        writeSlice(code, 'roster', SLICE_PAYLOAD.roster(current)),
        writeSlice(code, 'schedule', SLICE_PAYLOAD.schedule(current)),
        writeSlice(code, 'settings', SLICE_PAYLOAD.settings(current)),
        // Replaces the attendance document outright rather than merging, so
        // cells deleted locally are actually removed.
        writeSlice(code, 'attendance', current.attendance),
      ]);
      await touchTable(code, current.settings?.name).catch(() => {});

      // Same rule as the debounced flush: once the write settles, the refs and
      // the outbox may belong to a different table.
      if (isRemembered(code)) saveConfirmedAttendance(code, { ...current.attendance });
      if (codeRef.current !== code) return;

      lastAttendance.current = { ...current.attendance };
      confirmed.current = { ...current.attendance };
      dispatch({
        type: 'sync/drained',
        slices: ['roster', 'schedule', 'settings'],
        cells: [],
        attendanceReplace: true,
      });
      setError(null);
      setStatus('live');
    } catch (err) {
      if (codeRef.current === code) {
        setError(err);
        setStatus('error');
      }
      throw err;
    }
  }, [code, dispatch]);

  /* ------------------------------------------------------------ liveness */

  /**
   * Send anything still on the debounce before the page goes away.
   *
   * `visibilitychange` is the one the browser reliably delivers — `beforeunload`
   * is skipped on mobile and on tab discard. There is nothing to await: once
   * Firestore has accepted a write it is in the durable offline queue and will
   * be sent, on this page load or the next.
   */
  useEffect(() => {
    if (!code || !isFirebaseConfigured) return undefined;

    const flushIfHidden = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', flushIfHidden);
    window.addEventListener('pagehide', flush);

    return () => {
      document.removeEventListener('visibilitychange', flushIfHidden);
      window.removeEventListener('pagehide', flush);
    };
  }, [code, flush]);

  useEffect(() => {
    const update = () => {
      online.current = navigator.onLine;
      setStatus((current) => {
        if (current === 'off' || current === 'error') return current;
        return navigator.onLine ? 'live' : 'offline';
      });
    };
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return { status, error, flushNow: flush, pushAll };
}

/** The local marks whose key the server also has. */
function onlyKeysIn(local, server) {
  const out = {};
  for (const [key, value] of Object.entries(local)) if (key in server) out[key] = value;
  return out;
}

/**
 * The marks to remove once the server's own state is known: `{ key: null }`.
 *
 * The table was loaded from this browser's saved copy, and diffing the server
 * against that can add and change marks but, done naively, never remove one —
 * so a mark deleted elsewhere while this browser was away stayed on its screen,
 * in its scores, and went back to the server if anyone edited it.
 *
 * Removing everything the server lacks is worse: it cannot tell that mark from
 * one made here that never got there — a tab closed inside the debounce, a
 * refused write, a table carried over from the old single-document layout —
 * and deleting those deletes the only copy. So a mark goes only if the server
 * confirmed it with this value, it is still what was loaded and still what is
 * on screen, and the server no longer has it.
 */
export function removedSince(confirmedBefore, loaded, local, server) {
  const removed = {};
  for (const [key, value] of Object.entries(confirmedBefore)) {
    if (key in server) continue;
    if (loaded[key] === value && local[key] === value) removed[key] = null;
  }
  return removed;
}

/** Whether a table is still in this browser — a forgotten one gets no record. */
function isRemembered(code) {
  return listTables().some((entry) => entry.id === code);
}

/** The entries of `record` that `latest` still agrees with. */
function agreeing(record, latest) {
  const out = {};
  for (const [key, value] of Object.entries(record)) if (latest[key] === value) out[key] = value;
  return out;
}

/**
 * What stands in for the confirmed record before this browser has one for a
 * table — the first time it opens that table on this version.
 *
 * The saved copy is taken to have been in step with the server, which is how
 * every copy made by joining and syncing came about; without that, a mark
 * deleted elsewhere before the record existed would stay on this screen for
 * good. A copy carried over from the v1 layout never was in step, so nothing
 * in it is assumed. The cost is one-off and narrow: a mark made here that never
 * reached the server (a tab closed inside the debounce, a write the server
 * refused) is not kept on that first load. No one else had ever seen it.
 *
 * It applies whether or not the cache answered first. The cache follows the
 * server whenever this browser is listening, but the saved copy did not always
 * follow it — the previous version never removed a mark on load — so a mark
 * can be in the saved copy and in neither the cache nor the server, and only
 * this removes it. A mark handed to Firestore before the tab closed sits in
 * its durable queue and reaches the server, so it is not at risk.
 */
function assumedConfirmed(code, attendance) {
  return wasMigratedFromV1(code) ? null : attendance;
}

/** `cells` applied to a copy of `attendance`; null removes the cell. */
function applyCells(attendance, cells) {
  const next = { ...attendance };
  for (const [key, value] of Object.entries(cells)) {
    if (value === null) delete next[key];
    else next[key] = value;
  }
  return next;
}
